import api from './api';
import { uploadFile } from './uploads';
import type { UserProfile, User, UpdateProfileRequest, PaginatedResult, Review, TopReviewer, SidebarUser, SuggestedUser, RecentReview } from '../types';

export const usersService = {
  async getProfile(id: string): Promise<UserProfile> {
    const response = await api.get<UserProfile>(`/users/${id}/profile`);
    return response.data;
  },

  async getProfileByUsername(username: string): Promise<UserProfile> {
    const response = await api.get<UserProfile>(`/users/by-username/${username}/profile`);
    return response.data;
  },

  async follow(id: string): Promise<void> {
    await api.post(`/users/${id}/follow`);
  },

  async unfollow(id: string): Promise<void> {
    await api.delete(`/users/${id}/follow`);
  },

  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    const response = await api.put<User>('/users/me/profile', data);
    return response.data;
  },

  async uploadProfileImage(file: File): Promise<User> {
    // Upload file directly to Supabase Storage, then save URL to our API
    const imageUrl = await uploadFile(file, 'avatars');
    const response = await api.post<User>('/users/me/profile-image', { imageUrl });
    return response.data;
  },

  async getUserReviews(id: string, page = 1, pageSize = 10): Promise<PaginatedResult<Review>> {
    const response = await api.get<PaginatedResult<Review>>(`/users/${id}/reviews`, {
      params: { page, pageSize },
    });
    return response.data;
  },

  async getFollowers(id: string, page = 1, pageSize = 20): Promise<PaginatedResult<User>> {
    const response = await api.get<PaginatedResult<User>>(`/users/${id}/followers`, {
      params: { page, pageSize },
    });
    return response.data;
  },

  async getFollowing(id: string, page = 1, pageSize = 20, sort = 'recent'): Promise<PaginatedResult<User>> {
    const response = await api.get<PaginatedResult<User>>(`/users/${id}/following`, {
      params: { page, pageSize, sort },
    });
    return response.data;
  },

  async deleteAccount(): Promise<void> {
    await api.delete('/users/me');
  },

  async getTopReviewers(limit = 5): Promise<TopReviewer[]> {
    const response = await api.get<TopReviewer[]>('/analytics/top-reviewers', { params: { limit } });
    return response.data;
  },

  async getSimilarReviewers(username: string, limit = 3): Promise<SidebarUser[]> {
    const response = await api.get<SidebarUser[]>(`/users/by-username/${username}/similar`, { params: { limit } });
    return response.data;
  },

  async getTopReviewersByCategory(category: string, limit = 3): Promise<SidebarUser[]> {
    const response = await api.get<SidebarUser[]>('/users/top-reviewers', { params: { category, limit } });
    return response.data;
  },

  async getTopTags(username: string, limit = 5): Promise<import('../types').Hashtag[]> {
    const response = await api.get<import('../types').Hashtag[]>(`/users/by-username/${username}/top-tags`, { params: { limit } });
    return response.data;
  },

  async getSuggested(limit = 5): Promise<SuggestedUser[]> {
    const response = await api.get<SuggestedUser[]>('/users/suggested', { params: { limit } });
    return response.data;
  },

  async getRecentlyViewed(limit = 5): Promise<RecentReview[]> {
    const response = await api.get<RecentReview[]>('/users/me/recent-reviews', { params: { limit } });
    return response.data;
  },
};
